import React from "react";
import { Link } from "react-router-dom";

import Helmet from "../../components/Helmet";
import CallToAction from "../../components/CallToAction";
import MidCallToAction from "../../components/MidCallToAction";
import NavbarSettings from "../../components/navbar/NavbarSettings";
import SubPageBanner from "../../components/SubPageBanner";
import HomepageImageText from "../../components/HomepageImageText";

import zonnepanelenSchuur from "../../img/zonnepanelen/solar-zonnepanelen-schuurdak-min.jpg";
import zonnepanelenSchuinDak from "../../img/zonnepanelen/zonnepanelen-schuindak-5.jpg";

function ZonnepanelenTerugleverKosten() {
    return (
        <React.Fragment>
            <Helmet
                title="Terugleverkosten zonnepanelen"
                description="Hoe zit het met terugleverkosten van zonnepanelen? Wij vertellen je alles over terugverdientijd en slimme oplossingen om je rendement te maximaliseren."
            />

            <NavbarSettings>
                <SubPageBanner title="Zonnepanelen" subtitle="Terugleverkosten" image={zonnepanelenSchuur} />
            </NavbarSettings>

            <div className="container-orange-background mb-5">
                <div className="container">
                    <HomepageImageText
                        title="Terugleverkosten zonnepanelen"
                        text="Wanneer je zonnepanelen installeert, krijg je te maken met terugleverkosten. Dit zijn de kosten die energieleveranciers rekenen wanneer je de stroom die je zonnepanelen opwekken, teruglevert aan het energienet. Hoewel dit misschien klinkt als een nadeel, zijn deze kosten in verhouding laag. De voordelen van zonnepanelen blijven ruimschoots groter dan de terugleverkosten."
                        img={zonnepanelenSchuinDak}
                        imgAlt="Foto van zonnepanelen"
                        coverImage={true}
                    />
                </div>
            </div>

            <div className="container container-sm">
                <section className="container container-sm" aria-labelledby="how-it-works-title">
                    <h3 id="how-it-works-title">Hoe werken terugleverkosten voor zonnepanelen?</h3>
                    <p>
                        Wanneer je zonnepanelen meer stroom opwekken dan je verbruikt, lever je het overschot terug aan het net. Hiervoor krijg je een vergoeding, maar betaal je ook een klein bedrag aan terugleverkosten. Deze kosten zijn afhankelijk van je energieleverancier en het type contract dat je hebt. Gelukkig blijft de impact hiervan op je totale besparing gering. 
                    </p>
                </section>

                <section className="container container-sm" aria-labelledby="smart-investment-title">
                    <h3 id="smart-investment-title">
                        Zonnepanelen blijven een slimme investering
                    </h3>
                    <p>
                        Ondanks de terugleverkosten blijft de investering in zonnepanelen zeer rendabel. Met de huidige energieprijzen verdien je je installatie doorgaans binnen 4,5 tot 6 jaar terug. Daarna profiteer je jarenlang van goedkope energie. Dit maakt zonnepanelen een van de beste investeringen die je kunt doen, zelfs met de terugleverkosten in gedachten.
                    </p>
                </section>

                <section className="container container-sm" aria-labelledby="future-regulation-title">
                    <h3 id="future-regulation-title">
                        De toekomst van de salderingsregeling
                    </h3>
                    <p>
                        Op dit moment kun je de stroom die je teruglevert, salderen. Dat betekent dat je deze wegstreept tegen je eigen verbruik.
                        In de toekomst verdwijnt de salderingsregeling, namelijk in 2027. Toch is dit geen reden om te wachten met de aanschaf van zonnepanelen.
                        Tegen de tijd dat de saldering verdwijnt, heb je het grootste deel van je investering al terugverdiend.
                        Daarnaast kun je met slimme technologie, zoals een <Link to="/thuisbatterij/">thuisbatterij</Link>, nog steeds optimaal profiteren van je opgewekte stroom.
                    </p>
                </section>
            </div>

            <div className="container">
                <MidCallToAction />
            </div>

            <div className="container container-sm">
                <section className="container container-sm" aria-labelledby="high-prices-title">
                    <h3 id="high-prices-title">Hoge energieprijzen maken zonnepanelen extra aantrekkelijk</h3>
                    <p>
                        De huidige hoge energieprijzen maken het investeren in zonnepanelen aantrekkelijker dan ooit. Of je nu een variabel of vast contract hebt, de besparingen die je realiseert door zelf stroom op te wekken, zijn aanzienlijk. Zelfs als je terugleverkosten betaalt, blijft de terugverdientijd kort. Wil je weten wat zonnepanelen voor jouw situatie kosten? <Link to="/offerte/">Vraag dan een vrijblijvende offerte aan</Link>.
                    </p>
                </section>

                <section className="container container-sm" aria-labelledby="smart-control-title">
                    <h3 id="smart-control-title">Slimme sturing voor optimaal rendement</h3>
                    <p>
                        Met slimme sturing, zoals het gebruik van een thuisbatterij, kun je overtollige energie opslaan voor momenten waarop er minder energie opgewekt wordt. Dit zorgt ervoor dat je optimaal gebruikmaakt van je opgewekte energie en de terugleverkosten minimaliseert. Wil je meer weten over slimme sturing? Neem dan <Link to="/contact/">contact</Link> met ons op.
                    </p>
                </section>

                <section className="container container-sm" aria-labelledby="payback-time-title">
                    <h3 id="payback-time-title">Terugverdientijd van zonnepanelen met terugleverkosten</h3>
                    <p>
                        Zelfs met de terugleverkosten verdien je je investering in zonnepanelen vaak al binnen 4,5 tot 6 jaar terug. Daarna profiteer je van pure winst. Wil je een exacte berekening voor jouw situatie? Onze experts bij Smith Solar helpen je graag met een <Link to="/offerte/">offerte</Link>.
                    </p>
                </section>
            </div>

            <CallToAction />
        </React.Fragment>
    );
}

export default React.memo(ZonnepanelenTerugleverKosten);
